import axios from 'axios'
import { Buffer } from 'buffer'

const downloadFileFromBlob = (blob: Blob, filename: string, type: string) => {
  const url = window.URL.createObjectURL(
    type ? new Blob([blob], { type: type }) : new Blob([blob])
  )

  const link = document.createElement('a')
  link.href = `${url}`

  if (window.ReactNativeWebView) {
    postToApp(blob, filename)
    return
  }

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 100)
}

const downloadAttachment = (
  url: string,
  apiVersion = '1.0',
  responseType: any = 'blob',
  noAuth?: boolean
) => {
  if (noAuth)
    return axios.request({
      method: 'get',
      url: url,
      headers: {
        'x-api-version': apiVersion,
      },
      responseType,
      transformRequest: (data, headers: any) => {
        delete headers?.common['Authorization']
        return data
      },
    })

  if (apiVersion) {
    return axios.request({
      method: 'get',
      url: url,
      headers: {
        'x-api-version': apiVersion,
      },
      responseType,
    })
  }
  return axios.get(url, { responseType })
}

const openFileInNewTab = (
  data: any,
  filename: string,
  winobj?: any,
  type?: string
) => {
  var file = new Blob([data], { type: type ?? 'application/pdf' })
  var url = URL.createObjectURL(file)

  if (window.ReactNativeWebView) {
    postToApp(data, filename)
    return
  }

  if (winobj) winobj.location.href = url
  else window.open(url, '_blank')
}

const postToApp = (data: any, filename: string) => {
  const sanitizedFilename = getCleanFileName(filename)
  new Response(data)
    .arrayBuffer()
    .then((res) => {
      var base64 = Buffer.from(res).toString('base64')

      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'download',
          filename: sanitizedFilename,
          data: base64,
        })
      )
    })
    .catch((error) => {
      console.error('File could not be converted to base64', error)
    })
}

const getExtension = (filename: string) =>
  filename.includes('.') ? filename.toLowerCase().split('.').pop()! : ''

const isValidFileType = (
  filename: string,
  accepts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg']
) => {
  const fileExtension = getExtension(filename)
  return accepts.includes(fileExtension)
}

const isValidFileSizes = (
  files: File[],
  maxUploadSizeInBytes: number
): boolean => {
  // Summarize sizes of filearray and check if total valid size

  let totalFileSizes = 0

  files.map((file) => (totalFileSizes += file.size))

  return totalFileSizes <= maxUploadSizeInBytes
}

const hasInvalidFileTypes = (
  files: File[],
  invalidFileTypes: string[] // ex: ['.pdf']
): boolean => {
  // Check if filearray only contains valid filetypes

  if (!files || files.length === 0) return false
  const containsInvalid = files.find((file) => {
    const invalid = new RegExp(
      '^.*.(' + invalidFileTypes.join('|') + ')$',
      'g'
    ).test(file.name)
    return invalid
  })
  return !!containsInvalid
}

// Replaces "." and "/" in the filename with dashes, except for the last dot wich should be the file extension
const getCleanFileName = (filename: string) => {
  if (!filename) return ''
  filename = filename.replaceAll('/', '-')
  let parts = filename.split('.')

  // If there is only one part, return it as is (no dots to change)
  if (parts.length === 1) {
    return filename
  }

  // Combine all parts except the last one (the extension) and replace dots with dashes
  let cleanedName = parts.slice(0, -1).join('.').replace(/\./g, '-')

  // Reassemble the cleaned name and the file extension
  return cleanedName + '.' + parts[parts.length - 1]
}

const getFileNameWithSuffix = (title: string, type: string) => {
  return title + '.' + type
}

export {
  postToApp,
  downloadFileFromBlob,
  downloadAttachment,
  openFileInNewTab,
  getExtension,
  isValidFileType,
  isValidFileSizes,
  hasInvalidFileTypes,
  getCleanFileName,
  getFileNameWithSuffix,
}
